import {
    FINALIZED_LIFECYCLE_REPLAY_ORGANIZATION_IDS,
    isFinalizedLifecycleReplayOrganization,
} from '../RuntimeConfig';

export const FINALIZED_LIFECYCLE_REPLAY_VERSION = 'northstar-finalized-lifecycle-v1';
export const FINALIZED_LIFECYCLE_REPLAY_ORGANIZATION = 'Northstar Field Services Training';

export type FinalizedLifecycleCriterionKind = 'Ability' | 'Behavior' | 'Counterproductive behavior';
export type FinalizedLifecycleDecisionStatus = 'Accepted' | 'Rejected' | 'Finalized';

export interface FinalizedLifecycleCriterion {
    Category: string;
    Kind: FinalizedLifecycleCriterionKind;
    Name: string;
    Rating: string;
    Evidence: string;
}

export interface FinalizedLifecycleTranscriptMessage {
    Role: 'Northstar Trainer' | 'Synergist AI';
    Content: string;
}

export interface FinalizedLifecycleDecision {
    Status: FinalizedLifecycleDecisionStatus;
    Title: string;
    Detail: string;
}

export interface FinalizedLifecycleReplay {
    Version: string;
    IsSynthetic: true;
    ReadOnly: true;
    OrganizationId: string;
    OrganizationName: string;
    TalentName: string;
    PositionName: string;
    TemplateName: string;
    FinalizedDate: string;
    FinalizedBy: string;
    Criteria: FinalizedLifecycleCriterion[];
    Summary: string;
    DevelopmentAction: {
        Title: string;
        Narrative: string;
        Duration: string;
        CoachingCadence: string;
        Status: 'Approved — Active';
    };
    AiHistory: {
        AccessLabel: string;
        Status: 'Finalized — Read-only';
        Transcript: FinalizedLifecycleTranscriptMessage[];
        Decisions: FinalizedLifecycleDecision[];
    };
    NextCycle: {
        PriorReview: string;
        ActiveDevelopment: string;
        NewObservation: {
            Date: string;
            Title: string;
            Description: string;
        };
        Safeguard: string;
    };
}

export interface FinalizedLifecycleReplayRequest {
    selectedOrganizationId: string;
    selectedOrganizationName: string;
}

const deepFreeze = <T,>(value: T): T => {
    if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
        Object.values(value as Record<string, unknown>).forEach(deepFreeze);
        Object.freeze(value);
    }
    return value;
};

/**
 * Canonical TR-008 values. The capture specification contains this exact object
 * as JSON and tests compare it structurally, so rendered source and editorial
 * fixture language cannot drift independently.
 */
export const FINALIZED_LIFECYCLE_REPLAY_CANONICAL: Omit<FinalizedLifecycleReplay, 'OrganizationId'> = deepFreeze({
    Version: FINALIZED_LIFECYCLE_REPLAY_VERSION,
    IsSynthetic: true,
    ReadOnly: true,
    OrganizationName: FINALIZED_LIFECYCLE_REPLAY_ORGANIZATION,
    TalentName: 'Jordan Avery',
    PositionName: 'Field Service Technician',
    TemplateName: 'Field Service Technician Success Profile — revision 1',
    FinalizedDate: 'August 26, 2026',
    FinalizedBy: 'Northstar Trainer',
    Criteria: [
        {
            Category: 'Performant',
            Kind: 'Ability',
            Name: 'Inspects commercial equipment systematically',
            Rating: 'Advanced',
            Evidence: 'Completed eight scheduled inspections — July 12, 2026',
        },
        {
            Category: 'Communication',
            Kind: 'Behavior',
            Name: 'Documents service findings promptly',
            Rating: 'Usually',
            Evidence: 'Submitted service records on time — July 28, 2026',
        },
        {
            Category: 'Communication',
            Kind: 'Behavior',
            Name: 'Escalates ambiguous safety risks before continuing work',
            Rating: 'Sometimes',
            Evidence: 'Ambiguous safety condition required follow-up — August 6, 2026',
        },
        {
            Category: 'Sustainable',
            Kind: 'Counterproductive behavior',
            Name: 'Proceeds when safety conditions are unclear',
            Rating: 'Rarely',
            Evidence: 'Lower frequency is better for this counterproductive behavior.',
        },
    ],
    Summary: 'Jordan completed scheduled inspections and documented findings promptly. More consistent escalation of ambiguous safety conditions remains the development priority.',
    DevelopmentAction: {
        Title: 'Escalate ambiguous safety conditions consistently',
        Narrative: 'For the next 30 days, use the safety-escalation checklist on every ambiguous condition and review the completed entries with Northstar Trainer each week.',
        Duration: '30 days',
        CoachingCadence: 'Weekly review with Northstar Trainer',
        Status: 'Approved — Active',
    },
    AiHistory: {
        AccessLabel: 'Assigned supervisor and authorized administrators only',
        Status: 'Finalized — Read-only',
        Transcript: [
            {
                Role: 'Northstar Trainer',
                Content: 'During this review period, Jordan completed eight scheduled equipment inspections and submitted the service records on time. One ambiguous safety condition was documented but not escalated until I followed up. I want the review to recognize the consistent inspection and documentation work while making earlier safety escalation a clear development priority.',
            },
            {
                Role: 'Synergist AI',
                Content: 'The evidence supports consistent inspection and documentation work, while the follow-up shows that earlier escalation remains a development priority.',
            },
        ],
        Decisions: [
            {
                Status: 'Accepted',
                Title: 'Use the safety-escalation checklist consistently',
                Detail: 'Refined into the approved 30-day development action with weekly supervisor review.',
            },
            {
                Status: 'Rejected',
                Title: 'Redesign the full inspection program',
                Detail: 'Too broad and not supported by the specific review evidence.',
            },
            {
                Status: 'Finalized',
                Title: 'Supervisor publication decision',
                Detail: 'Northstar Trainer reviewed the evidence, decided on the proposals, and finalized the assessment.',
            },
        ],
    },
    NextCycle: {
        PriorReview: 'Latest finalized assessment — August 26, 2026',
        ActiveDevelopment: '30-day safety-escalation checklist action — active',
        NewObservation: {
            Date: 'September 9, 2026',
            Title: 'Used the safety-escalation checklist before continuing work',
            Description: 'Jordan used the checklist on an ambiguous equipment condition and reviewed the completed entry with Northstar Trainer.',
        },
        Safeguard: 'Context is available for the next review; prior answers are never copied forward automatically.',
    },
});

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

/**
 * An in-browser, GET-only synthetic fixture service. It never calls fetch and
 * exposes no mutation method, so capture cannot read from or write to a live
 * organization record.
 */
export default class FinalizedLifecycleReplayService {
    async Get(request: FinalizedLifecycleReplayRequest, signal?: AbortSignal): Promise<FinalizedLifecycleReplay> {
        if (signal?.aborted)
            throw new DOMException('The finalized lifecycle replay request was aborted.', 'AbortError');

        if (!isFinalizedLifecycleReplayOrganization(
            request.selectedOrganizationId,
            request.selectedOrganizationName,
            FINALIZED_LIFECYCLE_REPLAY_ORGANIZATION_IDS,
        ))
            throw new Error('Finalized lifecycle replay is not available for this organization.');

        await Promise.resolve();

        if (signal?.aborted)
            throw new DOMException('The finalized lifecycle replay request was aborted.', 'AbortError');

        return clone({ ...FINALIZED_LIFECYCLE_REPLAY_CANONICAL, OrganizationId: request.selectedOrganizationId });
    }
}

export const finalizedLifecycleReplayFingerprint = (replay: FinalizedLifecycleReplay) =>
    JSON.stringify({ ...replay, OrganizationId: '' });

export const expectedFinalizedLifecycleReplayFingerprint = () =>
    JSON.stringify({ ...FINALIZED_LIFECYCLE_REPLAY_CANONICAL, OrganizationId: '' });
